import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Card } from '@amore/ui/components/card'
import {
  SortableDataList,
  SortableDataListHandle,
  SortableDataListItem,
} from '@amore/ui/components/sortable-data-list'
import { Text } from '@amore/ui/components/typography'
import { Link } from '@tanstack/react-router'
import { Clock3, Megaphone, PencilLine, Plus, Users } from 'lucide-react'
import { newId, plural } from '@/lib/format'
import { dayMinutes, groupMinutes, groupTips } from '@/mock/photo'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { PhotoGroup, WeddingData } from '@/mock/types'
import { EmptyState } from '@/patterns/empty-state'
import { ScreenSection } from '@/patterns/screen'
import { useToast } from '@/shell/toaster'

type Guest = { name: string; attendance: string }

function guestDirectory(data: WeddingData) {
  return new Map<string, Guest>(
    data.parties.flatMap((party) =>
      party.persons.map((person) => [
        person.id,
        {
          name: `${person.firstName} ${person.lastName}`.trim() || 'Doprovod',
          attendance: person.attendance,
        },
      ]),
    ),
  )
}

/** "Kdo svolává": a guest, a member or a supplier, with what they are at the wedding. */
export function callerLabel(data: WeddingData) {
  const caller = data.photo.caller
  if (!caller) return null
  if (caller.type === 'supplier') {
    const supplier = data.suppliers.find((item) => item.id === caller.id)
    return supplier ? { name: supplier.name, role: 'Dodavatel' } : null
  }
  if (caller.type === 'member') {
    const member = data.members.find((item) => item.id === caller.id)
    return member ? { name: member.name, role: 'Spoluorganizátor' } : null
  }
  const guest = guestDirectory(data).get(caller.id)
  const witness = [data.ceremony.brideWitnessId, data.ceremony.groomWitnessId].includes(caller.id)
  return guest ? { name: guest.name, role: witness ? 'Svědek' : 'Host' } : null
}

function moveGroup(groups: PhotoGroup[], fromId: string, toId: string) {
  const from = groups.findIndex((item) => item.id === fromId)
  const to = groups.findIndex((item) => item.id === toId)
  const moved = groups[from]
  if (!moved || to < 0) return groups
  const next = groups.filter((item) => item.id !== fromId)
  next.splice(to, 0, moved)
  return next
}

export function PhotoGroupsPage({
  onPreview,
}: {
  onPreview: (title: string, description?: string) => void
}) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const { groups } = data.photo
  const guests = guestDirectory(data)
  const comingCount = [...guests.values()].filter((guest) => guest.attendance !== 'declined').length
  const minutes = groups.reduce((sum, item) => sum + groupMinutes(item), 0)
  const slot = data.schedule.find((event) => event.id === 's-groups' && !event.removed)
  const slotMinutes =
    slot?.time && slot.endTime ? dayMinutes(slot.endTime) - dayMinutes(slot.time) : null
  const caller = callerLabel(data)
  const tips = groupTips.filter((tip) => !groups.some((item) => item.name === tip))
  const setGroups = (next: (groups: PhotoGroup[]) => PhotoGroup[]) =>
    update((current) => ({
      ...current,
      photo: { ...current.photo, groups: next(current.photo.groups) },
    }))
  const addTip = (name: string) => {
    const added: PhotoGroup = {
      id: newId('grp'),
      name,
      everyone: name === 'Všichni hosté',
      guestIds: [],
      priority: 'must',
    }
    setGroups((current) => [...current, added])
    showToast({
      title: 'Skupina přidána',
      description: name,
      tone: 'success',
      action: {
        label: 'Vrátit zpět',
        altText: `Odebrat skupinu ${name}`,
        onClick: () => setGroups((current) => current.filter((item) => item.id !== added.id)),
      },
    })
  }

  return (
    <>
      <Card padding="none" className="overflow-hidden">
        <div className="grid divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <div className="space-y-1 p-5">
            <Text variant="small" tone="muted">
              Na seznamu
            </Text>
            <p className="text-2xl font-semibold tabular-nums">
              {groups.length} {plural(groups.length, 'skupina', 'skupiny', 'skupin')}
            </p>
            <Text variant="small" tone="muted">
              {groups.length ? `Zaberou asi ${minutes} minut` : 'Zatím žádné'}
            </Text>
          </div>
          <div className="space-y-1 p-5">
            <Text variant="small" tone="muted">
              V harmonogramu
            </Text>
            {slot ? (
              <>
                <p className="text-2xl font-semibold tabular-nums">
                  {slot.time}–{slot.endTime}
                </p>
                <Link
                  to={`/schedule/${slot.id}`}
                  className={
                    slotMinutes !== null && slotMinutes < minutes
                      ? 'text-sm font-semibold text-warning-foreground underline-offset-4 hover:underline'
                      : 'text-sm text-muted-foreground underline-offset-4 hover:text-action hover:underline'
                  }
                >
                  {slotMinutes !== null && slotMinutes < minutes
                    ? `Jen ${slotMinutes} min, skupiny potřebují ${minutes}`
                    : `${slot.title} · ${slotMinutes} min`}
                </Link>
              </>
            ) : (
              <>
                <p className="text-2xl font-semibold">—</p>
                <Link
                  to="/schedule"
                  className="text-sm text-muted-foreground underline-offset-4 hover:text-action hover:underline"
                >
                  Na skupinové focení zatím není čas
                </Link>
              </>
            )}
          </div>
          <div className="flex items-start justify-between gap-3 p-5">
            <div className="min-w-0 space-y-1">
              <Text variant="small" tone="muted" className="inline-flex items-center gap-1.5">
                <Megaphone className="size-3.5" aria-hidden="true" />
                Svolává
              </Text>
              <p className="truncate text-base font-semibold">{caller?.name ?? 'Zatím nikdo'}</p>
              <Text variant="small" tone="muted">
                {caller?.role ?? 'Nejčastěji moderátor nebo svědek'}
              </Text>
            </div>
            {canEdit ? (
              <Button
                appearance="ghost"
                tone="neutral"
                size="icon"
                aria-label="Upravit, kdo svolává"
                onClick={() => onPreview('Výběr, kdo svolává, připravujeme')}
              >
                <PencilLine className="size-4" aria-hidden="true" />
              </Button>
            ) : null}
          </div>
        </div>
      </Card>

      <ScreenSection
        title="Pořadí focení"
        description="Fotograf půjde podle tohoto seznamu. Rodinu a starší hosty dejte dopředu."
        action={
          canEdit ? (
            <Button
              appearance="outline"
              tone="neutral"
              onClick={() => onPreview('Přidání skupiny připravujeme')}
            >
              <Plus className="size-4" aria-hidden="true" />
              Přidat skupinu
            </Button>
          ) : undefined
        }
      >
        {groups.length ? (
          <Card padding="none" className="overflow-hidden">
            <SortableDataList
              aria-label="Skupinové fotky"
              appearance="divided"
              items={groups.map((item) => item.id)}
              onReorder={(from, to) => {
                if (canEdit && typeof from === 'string' && typeof to === 'string')
                  setGroups((current) => moveGroup(current, from, to))
              }}
              accessibility={{
                screenReaderInstructions: {
                  draggable:
                    'Mezerníkem skupinu zvednete, šipkami přesunete a mezerníkem položíte.',
                },
              }}
            >
              {groups.map((item, index) => (
                <GroupRow
                  key={item.id}
                  group={item}
                  index={index}
                  canEdit={canEdit}
                  guests={guests}
                  comingCount={comingCount}
                  onOpen={() => onPreview('Detail skupiny připravujeme', item.name)}
                />
              ))}
            </SortableDataList>
          </Card>
        ) : (
          <EmptyState
            icon={<Users />}
            title="S kým chcete mít fotku?"
            description={
              canEdit
                ? 'Rodina, svědci, přátelé. Začněte některým tipem níže, nebo si přidejte vlastní skupinu.'
                : 'Seznam skupinových fotek je zatím prázdný.'
            }
          />
        )}
      </ScreenSection>

      {canEdit && tips.length ? (
        <section aria-label="Tipy na skupiny" className="space-y-3">
          <Text variant="small" tone="muted">
            Tipy na skupiny. Klepnutím přidáte, lidi doplníte v detailu.
          </Text>
          <div className="flex flex-wrap gap-2">
            {tips.map((tip) => (
              <Button
                key={tip}
                appearance="outline"
                tone="neutral"
                size="sm"
                onClick={() => addTip(tip)}
              >
                <Plus className="size-3.5" aria-hidden="true" />
                {tip}
              </Button>
            ))}
          </div>
        </section>
      ) : null}
    </>
  )
}

function GroupRow({
  group,
  index,
  canEdit,
  guests,
  comingCount,
  onOpen,
}: {
  group: PhotoGroup
  index: number
  canEdit: boolean
  guests: Map<string, Guest>
  comingCount: number
  onOpen: () => void
}) {
  const people = group.guestIds.flatMap((id) => {
    const guest = guests.get(id)
    return guest ? [guest] : []
  })
  const declined = people.filter((guest) => guest.attendance === 'declined')
  const names = people.filter((guest) => guest.attendance !== 'declined').map((guest) => guest.name)
  const who = group.everyone
    ? `Všichni, kdo přijdou · ${comingCount} hostů`
    : names.length
      ? names.length > 4
        ? `${names.slice(0, 3).join(', ')} a další ${names.length - 3}`
        : names.join(', ')
      : 'Zatím bez lidí'
  return (
    <SortableDataListItem id={group.id} disabled={!canEdit}>
      <div className="group flex items-center gap-2 px-3 py-3.5 sm:gap-3 sm:px-5">
        {canEdit ? (
          <SortableDataListHandle
            label={`Přesunout ${group.name}`}
            className="size-9 opacity-50 group-hover:opacity-100 focus-visible:opacity-100"
          />
        ) : null}
        <span className="grid size-8 shrink-0 place-items-center rounded-full border border-border bg-background text-xs font-semibold text-action tabular-nums">
          {index + 1}
        </span>
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 rounded-control text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <span className="block text-sm font-semibold break-words">
            {group.name || 'Skupina bez názvu'}
          </span>
          <span className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Users className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{who}</span>
          </span>
        </button>
        <span className="hidden shrink-0 items-center gap-1 text-xs text-muted-foreground tabular-nums sm:inline-flex">
          <Clock3 className="size-3.5" aria-hidden="true" />
          {groupMinutes(group)} min
        </span>
        <div className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center">
          {declined.length ? (
            <Badge tone="warning">
              {declined.length === 1
                ? `${declined[0]?.name} nepřijde`
                : `${declined.length} nepřijdou`}
            </Badge>
          ) : null}
          <Badge tone={group.priority === 'must' ? 'accent' : 'neutral'}>
            {group.priority === 'must' ? 'Musí být' : 'Když bude čas'}
          </Badge>
        </div>
        {canEdit ? (
          <Button
            appearance="ghost"
            tone="neutral"
            size="icon"
            className="size-9"
            aria-label={`Upravit ${group.name}`}
            onClick={onOpen}
          >
            <PencilLine className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </SortableDataListItem>
  )
}
